/**
 * A recording made ready to be held for as long as a note is written.
 *
 * Every recording is a few seconds long and still at full strength at its last frame -- the
 * FluidR3 ones 3.13 s, the VSCO-2 ones cut to six before the player stops -- so played as it
 * comes a whole note at a slow tempo stops dead before it should. And each one starts after
 * a different stretch of near-silence, so a chord struck together is heard with some of its
 * instruments late. Both are fixed once, when the sample is decoded, rather than on every
 * note:
 *
 * - The silence before the sound is cut, so that `start(at)` means the sound starts at `at`.
 * - A sustained instrument's steady end is made into a loop: the loop's last quarter
 *   second is blended into the frames just before its start, so the jump back is not heard,
 *   and the loop is a whole number of the note's own periods long, so the two things being
 *   blended are in step. It starts where the level has settled to what it is at the end:
 *   a soft string note still swelling seconds in would otherwise jump back down every time
 *   it came round. A struck or plucked sound dies away on its own and is not looped.
 */

/** A decoded recording and where it may be looped. */
export interface Prepared {
  readonly buffer: AudioBuffer;
  /** Seconds into `buffer` the sustain repeats between, or `null` to play it once. */
  readonly loop: { readonly start: number; readonly end: number } | null;
}

/** How far above silence the sound has to be to have started, as a fraction of its peak. */
const ONSET = 0.02;

/** Seconds kept before that, so the very front of the attack is not shaved off. */
const PREROLL = 0.002;

/** Seconds into the sound the loop may start at the earliest: past the attack. */
const LOOP_FROM = 0.7;

/** Seconds a loop is at least, where the level never settles for that long. */
const LOOP_AT_LEAST = 1;

/** Seconds the level is measured over when looking for where it has settled. */
const WINDOW = 0.1;

/** How far from the level at the end, as a ratio, the sound may be and count as settled: 4 dB. */
const SETTLED = 10 ** (4 / 20);

/** Seconds left unused at the end of a recording, where it may already be tailing off. */
const LOOP_SPARE = 0.12;

/** Seconds over which the end of the loop is blended into what precedes its start. */
const CROSSFADE = 0.25;

/** The frame the sound starts at, a little before it first rises above {@link ONSET}. */
function onsetOf(sound: AudioBuffer): number {
  const channels: Float32Array[] = [];
  for (let at = 0; at < sound.numberOfChannels; at += 1) channels.push(sound.getChannelData(at));
  let peak = 0;
  for (const wave of channels) for (const value of wave) peak = Math.max(peak, Math.abs(value));
  const floor = peak * ONSET;
  for (let frame = 0; frame < sound.length; frame += 1) {
    if (channels.some((wave) => Math.abs(wave[frame] ?? 0) > floor)) {
      return Math.max(0, frame - Math.round(PREROLL * sound.sampleRate));
    }
  }
  return 0;
}

function rmsOf(channels: readonly Float32Array[], from: number, to: number): number {
  let sum = 0;
  for (const wave of channels) for (let at = from; at < to; at += 1) sum += (wave[at] ?? 0) ** 2;
  return Math.sqrt(sum / Math.max(1, (to - from) * channels.length));
}

/**
 * The frame, counted from `from`, the loop starts at: the start of the stretch before
 * `usable` whose level stays within {@link SETTLED} of the level at its end, but no earlier
 * than {@link LOOP_FROM}, and early enough for a loop of {@link LOOP_AT_LEAST}.
 */
function loopStartOf(sound: AudioBuffer, from: number, usable: number): number {
  const rate = sound.sampleRate;
  const channels: Float32Array[] = [];
  for (let at = 0; at < sound.numberOfChannels; at += 1) {
    channels.push(sound.getChannelData(at).subarray(from, from + usable));
  }
  const earliest = Math.round(LOOP_FROM * rate);
  const width = Math.round(WINDOW * rate);
  const end = rmsOf(channels, usable - width, usable);
  let start = usable - width;
  while (start - width >= earliest) {
    const level = rmsOf(channels, start - width, start);
    if (level > end * SETTLED || level * SETTLED < end) break;
    start -= width;
  }
  return Math.max(earliest, Math.min(start, usable - Math.round(LOOP_AT_LEAST * rate)));
}

/**
 * `sound` with its leading silence cut and, for a `sustained` instrument, a seamless loop
 * written into its end. `hz` is the pitch it sounds, which the loop is measured in.
 */
export function prepare(
  context: BaseAudioContext,
  sound: AudioBuffer,
  hz: number,
  sustained: boolean,
): Prepared {
  const rate = sound.sampleRate;
  const from = onsetOf(sound);
  const length = sound.length - from;

  const usable = length - Math.round(LOOP_SPARE * rate);
  const loopStart = sustained ? loopStartOf(sound, from, usable) : 0;
  const fade = Math.round(CROSSFADE * rate);
  const room = usable - loopStart;
  const period = rate / hz;
  // Whole periods, rounded down so the loop never reaches into the spare end.
  const span = Math.floor(Math.floor(room / period) * period);
  const looped = sustained && span >= 2 * fade;
  const loopEnd = loopStart + span;

  const buffer = context.createBuffer(sound.numberOfChannels, looped ? loopEnd : length, rate);
  for (let channel = 0; channel < sound.numberOfChannels; channel += 1) {
    const wave = sound.getChannelData(channel).subarray(from, from + buffer.length);
    const out = new Float32Array(wave);
    if (looped) {
      // At `loopEnd - 1` the blend is all of the frame before `loopStart`, so the step from
      // there back to `loopStart` is one ordinary frame of the recording.
      for (let frame = loopEnd - fade; frame < loopEnd; frame += 1) {
        const into = (frame - (loopEnd - fade) + 1) / fade;
        out[frame] = (wave[frame] ?? 0) * (1 - into) + (wave[frame - span] ?? 0) * into;
      }
    }
    buffer.copyToChannel(out, channel);
  }

  return {
    buffer,
    loop: looped ? { start: loopStart / rate, end: loopEnd / rate } : null,
  };
}
